import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Shield,
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Lock,
  Eye,
  EyeOff,
  KeyRound,
  RefreshCw,
  Copy,
  Check,
  Home,
} from "lucide-react";
import { Progress } from "@/components/ui/progress";
import {
  TURNSTILE_SITE_KEY,
  EMAIL_DASHBOARD_EMAIL,
  EMAIL_DASHBOARD_PASSWORD,
  EMAIL_DASHBOARD_VERIFICATION_KEY,
} from "@/constants/auth";

// Define window.turnstile for TypeScript
declare global {
  interface Window {
    turnstile: any;
    onTurnstileVerified: (token: string) => void;
    onloadTurnstileCallback?: () => void;
  }
}

export function EmailVerification() {
  const navigate = useNavigate();
  const turnstileWidgetId = useRef<number | null>(null);
  const turnstileContainerRef = useRef<HTMLDivElement>(null);

  // Verification states
  const [turnstileVerified, setTurnstileVerified] = useState(false);
  const [mathVerified, setMathVerified] = useState(false);
  const [passwordVerified, setPasswordVerified] = useState(false);

  // Input states
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mathAnswer, setMathAnswer] = useState("");
  const [mathProblem, setMathProblem] = useState({
    num1: 0,
    num2: 0,
    answer: 0,
  });

  // UI states
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState(false);
  const [turnstileLoading, setTurnstileLoading] = useState(true);

  // Handle reset verification
  const handleReset = () => {
    // Clear the verification state
    localStorage.removeItem(EMAIL_DASHBOARD_VERIFICATION_KEY);
    // Reset all verification states
    setTurnstileVerified(false);
    setMathVerified(false);
    setPasswordVerified(false);
    setCurrentStep(1);
    setPassword("");
    setMathAnswer("");
    generateMathProblem();
    // Reload the page to reset Turnstile
    window.location.reload();
  };

  // Calculate verification progress
  const verificationProgress = [
    turnstileVerified ? 33.3 : 0,
    mathVerified ? 33.3 : 0,
    passwordVerified ? 33.4 : 0,
  ].reduce((a, b) => a + b, 0);

  // Generate a simple 1-digit math problem
  const generateMathProblem = () => {
    const num1 = Math.floor(Math.random() * 9) + 1; // 1-9
    const num2 = Math.floor(Math.random() * 9) + 1; // 1-9
    const answer = num1 + num2;
    setMathProblem({ num1, num2, answer });
  };

  // Force dark mode for this component
  useEffect(() => {
    // Add dark class to html element
    document.documentElement.classList.add("dark");

    // Remove it when component unmounts
    return () => {
      document.documentElement.classList.remove("dark");
    };
  }, []);

  // Initialize the math problem and check for existing verification
  useEffect(() => {
    generateMathProblem();

    // Check if the user is already verified (from localStorage)
    const verified = localStorage.getItem(EMAIL_DASHBOARD_VERIFICATION_KEY);
    if (verified === "true") {
      // Redirect to the admin email dashboard
      navigate("/admin/email");
    }
  }, [navigate]);

  // Handle Turnstile loading separately
  useEffect(() => {
    // Set loading to false - we're removing the skeleton
    setTurnstileLoading(false);

    // Direct implementation without skeleton
    const renderTurnstile = () => {
      if (
        window.turnstile &&
        turnstileContainerRef.current &&
        !turnstileWidgetId.current
      ) {
        turnstileWidgetId.current = window.turnstile.render(
          turnstileContainerRef.current,
          {
            sitekey: TURNSTILE_SITE_KEY,
            callback: handleTurnstileVerification,
            theme: "dark",
          }
        );
      }
    };

    // Check if Turnstile is already loaded
    if (window.turnstile) {
      renderTurnstile();
    } else {
      // Load Turnstile script
      const script = document.createElement("script");
      script.src =
        "https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onloadTurnstileCallback";
      script.async = true;
      script.defer = true;

      // Define global callback
      window.onloadTurnstileCallback = () => {
        renderTurnstile();
      };

      document.body.appendChild(script);
    }

    return () => {
      // Clean up when component unmounts
      if (window.turnstile && turnstileWidgetId.current !== null) {
        window.turnstile.remove(turnstileWidgetId.current);
      }
      // Remove global callback
      delete window.onloadTurnstileCallback;
    };
  }, []);

  // Handle Cloudflare Turnstile verification
  const handleTurnstileVerification = (token: string) => {
    if (token) {
      setLoading(true);
      // In a real implementation, you would verify the token with your server
      // For demo purposes, we'll just simulate a successful verification
      setTimeout(() => {
        setTurnstileVerified(true);
        setCurrentStep(2); // Move to math verification
        setLoading(false);
      }, 1000);
    }
  };

  // Handle math verification
  const handleMathVerification = () => {
    setLoading(true);
    setError("");

    if (parseInt(mathAnswer) === mathProblem.answer) {
      setTimeout(() => {
        setMathVerified(true);
        setCurrentStep(3); // Move to password verification
        setLoading(false);
      }, 1000);
    } else {
      setError("Incorrect answer. Please try again.");
      generateMathProblem();
      setMathAnswer("");
      setLoading(false);
    }
  };

  // Handle admin credentials verification
  const handleCredentialsVerification = () => {
    setLoading(true);
    setError("");

    // For AWS SES review team - use the provided credentials
    if (
      email === EMAIL_DASHBOARD_EMAIL &&
      password === EMAIL_DASHBOARD_PASSWORD
    ) {
      setTimeout(() => {
        setPasswordVerified(true);
        // All steps are verified, grant access
        localStorage.setItem(EMAIL_DASHBOARD_VERIFICATION_KEY, "true");
        setLoading(false);
        navigate("/admin/email");
      }, 1000);
    } else {
      setError("Incorrect email or password. Please try again.");
      setPassword("");
      setLoading(false);
    }
  };

  // Check if all verifications are complete
  const allVerified = turnstileVerified && mathVerified && passwordVerified;

  // Render the verification steps
  const renderVerificationStep = () => {
    switch (currentStep) {
      case 1: // Turnstile verification
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium">Step 1: Verify as Human</h3>
              {turnstileVerified && (
                <span className="text-green-600 dark:text-green-500 flex items-center">
                  <CheckCircle2 className="h-5 w-5 mr-1" />
                  Verified
                </span>
              )}
            </div>
            <div className="flex flex-col items-center justify-center space-y-4">
              {!turnstileVerified ? (
                <>
                  <div
                    ref={turnstileContainerRef}
                    className="cf-turnstile mx-auto"
                  ></div>
                  <p className="text-sm text-muted-foreground mt-2">
                    Please complete the Cloudflare Turnstile challenge to verify
                    you are human.
                  </p>
                </>
              ) : (
                <div className="text-center py-4">
                  <CheckCircle2 className="h-12 w-12 text-green-600 dark:text-green-500 mx-auto mb-2" />
                  <p>Human verification complete!</p>
                </div>
              )}
            </div>
            {turnstileVerified && (
              <button
                type="button"
                onClick={() => setCurrentStep(2)}
                className="gradient-border-button w-full py-2 text-gray-300"
              >
                Continue to Step 2
              </button>
            )}
          </div>
        );

      case 2: // Math verification
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium">
                Step 2: Simple Math Challenge
              </h3>
              {mathVerified && (
                <span className="text-green-600 dark:text-green-500 flex items-center">
                  <CheckCircle2 className="h-5 w-5 mr-1" />
                  Verified
                </span>
              )}
            </div>
            {!mathVerified ? (
              <div className="space-y-4">
                <div className="flex flex-col items-center justify-center">
                  <div className="text-2xl font-bold mb-4">
                    {mathProblem.num1} + {mathProblem.num2} = ?
                  </div>
                  <p className="text-sm text-muted-foreground mb-4">
                    Please solve this simple 1-digit addition problem.
                  </p>
                  <div className="w-full max-w-xs">
                    <Label htmlFor="math-answer">Your Answer</Label>
                    <Input
                      id="math-answer"
                      type="number"
                      value={mathAnswer}
                      onChange={(e) => setMathAnswer(e.target.value)}
                      placeholder="Enter your answer"
                      className="mt-1"
                    />
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleMathVerification}
                  disabled={!mathAnswer || loading}
                  className="gradient-border-button w-full py-2 text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed mb-2"
                >
                  {loading ? "Verifying..." : "Verify Answer"}
                </button>
                <button
                  type="button"
                  onClick={generateMathProblem}
                  className="border border-gray-700 w-full py-2 text-gray-400 rounded-md hover:bg-gray-800 transition-colors"
                >
                  Generate New Problem
                </button>
              </div>
            ) : (
              <div className="text-center py-4">
                <CheckCircle2 className="h-12 w-12 text-green-600 dark:text-green-500 mx-auto mb-2" />
                <p>Math challenge complete!</p>
              </div>
            )}
            <div className="flex justify-between">
              <button
                type="button"
                onClick={() => setCurrentStep(1)}
                className="border border-gray-700 px-4 py-2 text-gray-400 rounded-md hover:bg-gray-800 transition-colors"
              >
                Back to Step 1
              </button>
              {mathVerified && (
                <button
                  type="button"
                  onClick={() => setCurrentStep(3)}
                  className="gradient-border-button px-4 py-2 text-gray-300"
                >
                  Continue to Step 3
                </button>
              )}
            </div>
          </div>
        );

      case 3: // Admin credentials verification
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium">
                Step 3: Enter Admin Credentials
              </h3>
              {passwordVerified && (
                <span className="text-green-600 dark:text-green-500 flex items-center">
                  <CheckCircle2 className="h-5 w-5 mr-1" />
                  Verified
                </span>
              )}
            </div>
            {!passwordVerified ? (
              <div className="space-y-4">
                <div className="w-full">
                  <Label htmlFor="email">Admin Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter the admin email"
                    className="mt-1"
                  />
                </div>

                <div className="w-full">
                  <Label htmlFor="password">Admin Password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter the admin password"
                    className="mt-1"
                  />
                </div>

                <p className="text-xs text-muted-foreground mt-1">
                  For AWS SES review team use only.
                </p>

                <button
                  type="button"
                  onClick={handleCredentialsVerification}
                  disabled={!email || !password || loading}
                  className="gradient-border-button w-full py-2 text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? "Verifying..." : "Verify Credentials"}
                </button>
              </div>
            ) : (
              <div className="text-center py-4">
                <CheckCircle2 className="h-12 w-12 text-green-600 dark:text-green-500 mx-auto mb-2" />
                <p>Admin verification complete!</p>
                <button
                  type="button"
                  onClick={() => navigate("/admin/email")}
                  className="gradient-border-button px-6 py-2 text-gray-300 mt-4"
                >
                  Access Email Dashboard
                </button>
              </div>
            )}
            <button
              type="button"
              onClick={() => setCurrentStep(2)}
              className="border border-gray-700 px-4 py-2 text-gray-400 rounded-md hover:bg-gray-800 transition-colors mt-4"
            >
              Back to Step 2
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-blue-950 to-black p-4 dark">
      {/* Animated border lines */}
      <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-blue-500/30 to-transparent"></div>
      <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-blue-500/30 to-transparent"></div>
      <div className="absolute left-0 top-0 bottom-0 w-[1px] bg-gradient-to-b from-transparent via-blue-500/30 to-transparent"></div>
      <div className="absolute right-0 top-0 bottom-0 w-[1px] bg-gradient-to-b from-transparent via-blue-500/30 to-transparent"></div>
      <Card className="w-full max-w-md card-wrapper">
        <div className="card-content">
          <CardHeader className="text-center">
            <div className="mx-auto bg-blue-100 dark:bg-blue-900 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4">
              <Shield className="h-6 w-6 text-blue-700 dark:text-blue-300" />
            </div>
            <CardTitle className="text-2xl">
              Email Dashboard Verification
            </CardTitle>
            <CardDescription className="mt-2 mb-2">
              Complete all three verification steps to access the email
              dashboard
            </CardDescription>

            <div className="divider-line"></div>

            <div className="mt-4">
              <div className="flex justify-between mb-2">
                <span className="text-sm font-medium text-emerald-500">
                  Verification Progress
                </span>
                <span className="text-sm font-medium text-emerald-500">
                  {Math.round(verificationProgress)}%
                </span>
              </div>
              <Progress
                value={verificationProgress}
                className="h-2 bg-blue-950"
                indicatorClassName="bg-gradient-to-r from-green-500 to-emerald-500"
              />

              <div className="flex justify-between mt-4 text-xs text-muted-foreground">
                <div className="flex flex-col items-center">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      turnstileVerified
                        ? "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300"
                        : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {turnstileVerified ? (
                      <CheckCircle2 className="h-4 w-4" />
                    ) : (
                      "1"
                    )}
                  </div>
                  <span className="mt-1">Human</span>
                </div>
                <div className="flex flex-col items-center">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      mathVerified
                        ? "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300"
                        : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {mathVerified ? <CheckCircle2 className="h-4 w-4" /> : "2"}
                  </div>
                  <span className="mt-1">Math</span>
                </div>
                <div className="flex flex-col items-center">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      passwordVerified
                        ? "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300"
                        : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {passwordVerified ? (
                      <CheckCircle2 className="h-4 w-4" />
                    ) : (
                      "3"
                    )}
                  </div>
                  <span className="mt-1">Password</span>
                </div>
              </div>
            </div>
          </CardHeader>

          <CardContent className="pt-6">
            <div className="bg-yellow-100 dark:bg-yellow-900/40 border border-yellow-300 dark:border-yellow-700 rounded-md p-3 mb-6">
              <div className="flex">
                <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-500 mr-2 flex-shrink-0" />
                <p className="text-sm text-yellow-800 dark:text-yellow-300">
                  <span className="font-medium">AWS SES Review Notice:</span>{" "}
                  This verification page is specifically created for AWS SES
                  production access review. It demonstrates our security
                  measures to protect the email dashboard.
                </p>
              </div>
            </div>

            {renderVerificationStep()}

            {error && (
              <Alert variant="destructive" className="mt-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="divider-line mt-6 mb-2"></div>
          </CardContent>

          <CardFooter className="flex flex-col space-y-4">
            <p className="text-xs text-center text-muted-foreground">
              This verification is in place to protect the email dashboard from
              unauthorized access. For AWS SES production access review
              purposes.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button
                type="button"
                className="gradient-border-button flex items-center justify-center gap-1 text-gray-300 px-4 py-2 rounded-md text-sm"
                onClick={handleReset}
              >
                <RefreshCw className="h-3.5 w-3.5" />
                <span>Reset Verification</span>
              </button>

              <button
                type="button"
                className="gradient-border-button flex items-center justify-center gap-1 text-gray-300 px-4 py-2 rounded-md text-sm"
                onClick={() => (window.location.href = "/")}
              >
                <Home className="h-3.5 w-3.5" />
                <span>Go to Home Page</span>
              </button>
            </div>

            <p className="text-xs text-center text-blue-400/60 mt-4">
              © {new Date().getFullYear()} Numberz Insight |{" "}
              <span className="text-gray-500">v1.0.0</span>
            </p>
          </CardFooter>
        </div>
      </Card>
    </div>
  );
}
